import { Outlet } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useSidebar } from '@/contexts/SidebarContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Button, Badge, MenuIcon } from '@/components/ui';
import { Sidebar } from './Sidebar';
import { SyncStatusBadge } from './SyncStatusBadge';
import { PipelineStatusBadge } from './PipelineStatusBadge';
import { getRoleBadgeVariant } from '@/lib/role-utils';
import { useHeaderStatus } from '@/hooks/use-header-status';

export function AppLayout() {
  const { user, logout } = useAuth();
  const { toggleMobile } = useSidebar();
  const { theme, toggleTheme } = useTheme();
  const { sync, syncLoading, pipeline, pipelineLoading } = useHeaderStatus();

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />

      {/* Main content area */}
      <div
        className={cn(
          'flex flex-col transition-[margin] duration-300 ease-in-out',
          // Desktop: always offset by sidebar width (w-64)
          'md:ml-64'
        )}
      >
        {/* Header */}
        <header className="sticky top-0 z-30 border-b border-border bg-card">
          <div className="flex h-16 items-center justify-between px-4 md:px-6">
            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden"
              onClick={toggleMobile}
              aria-label="Open menu"
            >
              <MenuIcon className="h-5 w-5" />
            </Button>

            {/* Spacer for desktop (title is in sidebar) */}
            <div className="hidden md:block" />

            {/* User info and logout */}
            {user && (
              <div className="flex items-center gap-4">
                <div className="hidden sm:flex items-center gap-2">
                  <SyncStatusBadge summary={sync} isLoading={syncLoading} />
                  <PipelineStatusBadge summary={pipeline} isLoading={pipelineLoading} />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground hidden sm:inline">
                    {user.email}
                  </span>
                  <Badge variant={getRoleBadgeVariant(user.role)}>
                    {user.role}
                  </Badge>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleTheme}
                  aria-pressed={theme === 'dark'}
                >
                  {theme === 'dark' ? 'Light mode' : 'Dark mode'}
                </Button>
                <Button variant="ghost" size="sm" onClick={logout}>
                  Log out
                </Button>
              </div>
            )}
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 md:p-6 lg:p-8">
          <div className="w-full">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
